import styles from './DateTimePicker.module.css';

interface DateTimePickerProps {
  disabled?: boolean;
  id?: string;
  label: string;
  max?: string;
  min?: string;
  onChange: (utcMillis: number | null) => void;
  value: number | null;
}

function formatDatePart(value: number) {
  return String(value).padStart(2, '0');
}

export function utcMillisToLocalDateTimeString(utcMillis: number): string {
  const date = new Date(utcMillis);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = formatDatePart(date.getMonth() + 1);
  const day = formatDatePart(date.getDate());
  const hours = formatDatePart(date.getHours());
  const minutes = formatDatePart(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function localDateTimeStringToUtcMillis(value: string): number | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  const time = date.getTime();

  return Number.isNaN(time) ? null : time;
}

function normalizeDateTimeBoundary(value: string | undefined) {
  if (!value) {
    return undefined;
  }

  const isLocalDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
  const hasTimezone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value);

  if (isLocalDateTime && !hasTimezone) {
    return value.slice(0, 16);
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? value
    : utcMillisToLocalDateTimeString(parsed.getTime());
}

export function DateTimePicker({
  disabled = false,
  id,
  label,
  max,
  min,
  onChange,
  value,
}: DateTimePickerProps) {
  const inputId = id ?? label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return (
    <div className={styles.wrapper}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <input
        className={styles.input}
        disabled={disabled}
        id={inputId}
        max={normalizeDateTimeBoundary(max)}
        min={normalizeDateTimeBoundary(min)}
        onChange={(event) =>
          onChange(localDateTimeStringToUtcMillis(event.target.value))
        }
        type="datetime-local"
        value={value === null ? '' : utcMillisToLocalDateTimeString(value)}
      />
    </div>
  );
}
